import { User } from 'oidc-client-ts';
import {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  getUser,
  login as oidcSignIn,
  logout as oidcSignOut,
  userManager,
} from '@savgroup-front-common/core/services/userManager';

import { ChoosePlanProviderContainer } from '../components/ChoosePlanDrawer/ChoosePlanDrawer.context';

import { AppProvider } from './App.context.base';
import { useIframeContext, useSellerSelection } from './App.context.hooks';
import { AppLoginArgs, AppLogoutArgs } from './App.context.types';

export const OidcAppContextProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const { selectedSeller, onSelectSeller } = useSellerSelection();
  const { isIframeContext, onIframePageMount, onIframePageUnmount } =
    useIframeContext();

  const [user, setUser] = useState<User | undefined>(undefined);

  useEffect(() => {
    getUser()
      .then((currentUser) => {
        setUser(currentUser ?? undefined);
      })
      .catch(() => {
        setUser(undefined);
      });

    const handleUserLoaded = (loadedUser: User) => {
      setUser(loadedUser);
    };

    const handleUserSignedOut = () => {
      setUser(undefined);
    };

    const handleAccessTokenExpired = () => {
      setUser(undefined);
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserSignedOut(handleUserSignedOut);
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired);

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserSignedOut(handleUserSignedOut);
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
    };
  }, []);

  const login = useCallback(
    (args?: AppLoginArgs) =>
      oidcSignIn({
        extraQueryParams: args?.extraQueryParams,
        state: args?.returnTo ? { redirectUrl: args.returnTo } : args?.state,
        resource: args?.resource,
      }),
    [],
  );

  const logout = useCallback(
    (args?: AppLogoutArgs) =>
      oidcSignOut({
        post_logout_redirect_uri: args?.postLogoutRedirectUri,
      }),
    [],
  );

  return (
    <AppProvider
      value={{
        isIframeContext,
        onIframePageMount,
        onIframePageUnmount,
        selectedSeller,
        onSelectSeller,
        isAuthenticated: user !== undefined,
        user,
        login,
        logout,
      }}
    >
      <ChoosePlanProviderContainer>{children}</ChoosePlanProviderContainer>
    </AppProvider>
  );
};
